import { usePublicFeatureConfig } from 'hooks/usePublicFeatureConfig'
import { useMemo } from 'react'
import { useFeatureFlag } from 'util/featureFlags'
import {
  DEFAULT_SHOP_CATEGORIES,
  DEFAULT_SHOP_CATEGORY_CONFIG,
  OTHER_SUBTYPES,
  SHOP_CATEGORY_CONTROL_KEY,
} from './constants'
import { ComponentType, OtherSubtype, ShopCategory, ShopCategoryConfig } from './types'

interface UseShopCategoryConfigResult {
  config: ShopCategoryConfig
  categories: ShopCategory[]
  enabledCategories: ShopCategory[]
  isLoading: boolean
  hasCustomConfig: boolean
  isEmptyConfig: boolean
  getCategoryBySearchKey: (searchKey: string) => ShopCategory | undefined
  getCategoryByComponentType: (componentType: ComponentType, subtype?: OtherSubtype) => ShopCategory | undefined
  getCategoryLabel: (typeOrSearchKey: ComponentType | string, subtype?: OtherSubtype) => string
  getSubtypesForCategory: (category: ShopCategory) => OtherSubtype[]
}

const ALLOWED_COMPONENT_TYPES: ComponentType[] = ['module', 'inverter', 'battery', 'other']

const validateCategory = (category: unknown): category is ShopCategory => {
  if (!category || typeof category !== 'object') return false
  const entry = category as Record<string, unknown>

  if (
    typeof entry.id !== 'string' ||
    typeof entry.name !== 'string' ||
    typeof entry.componentType !== 'string' ||
    typeof entry.searchKey !== 'string' ||
    typeof entry.order !== 'number' ||
    typeof entry.enabled !== 'boolean'
  ) {
    return false
  }

  if (!ALLOWED_COMPONENT_TYPES.includes(entry.componentType as ComponentType)) {
    return false
  }

  const subtypes = entry.subtypes
  if (subtypes !== undefined) {
    if (!Array.isArray(subtypes)) {
      return false
    }
    for (const subtype of subtypes) {
      if (typeof subtype !== 'string') {
        return false
      }
      if (subtype !== '*' && !OTHER_SUBTYPES.includes(subtype as OtherSubtype)) {
        return false
      }
    }
  }

  return true
}

const validateConfig = (config: unknown): config is ShopCategoryConfig => {
  if (!config || typeof config !== 'object') return false
  const cfg = config as Record<string, unknown>
  if (!Array.isArray(cfg.categories)) return false

  if (!cfg.categories.every(validateCategory)) {
    return false
  }

  const seenIds = new Set<string>()
  const seenSearchKeys = new Set<string>()
  for (const category of cfg.categories as ShopCategory[]) {
    if (seenIds.has(category.id) || seenSearchKeys.has(category.searchKey)) {
      return false
    }
    seenIds.add(category.id)
    seenSearchKeys.add(category.searchKey)
  }

  return true
}

export const useShopCategoryConfig = (): UseShopCategoryConfigResult => {
  const isShopUpliftEnabled = useFeatureFlag('shop_uplift', 'on')
  const rawConfig = usePublicFeatureConfig(SHOP_CATEGORY_CONTROL_KEY)

  const config = useMemo<ShopCategoryConfig>(() => {
    if (!isShopUpliftEnabled) {
      return DEFAULT_SHOP_CATEGORY_CONFIG
    }

    if (!rawConfig) {
      return DEFAULT_SHOP_CATEGORY_CONFIG
    }

    if (!validateConfig(rawConfig)) {
      console.warn('[useShopCategoryConfig] Invalid config structure, using defaults')
      return DEFAULT_SHOP_CATEGORY_CONFIG
    }

    const validCategories = rawConfig.categories.filter((cat) => {
      if (!validateCategory(cat)) {
        console.warn(`[useShopCategoryConfig] Invalid category entry, skipping:`, cat)
        return false
      }
      return true
    })

    if (validCategories.length === 0) {
      console.warn('[useShopCategoryConfig] No valid categories in config, using defaults')
      return DEFAULT_SHOP_CATEGORY_CONFIG
    }

    return { categories: validCategories }
  }, [isShopUpliftEnabled, rawConfig])

  const categories = useMemo(() => {
    return [...config.categories].sort((a, b) => a.order - b.order)
  }, [config])

  const enabledCategories = useMemo(() => {
    return categories.filter((cat) => cat.enabled)
  }, [categories])

  const getCategoryBySearchKey = useMemo(() => {
    const searchKeyMap = new Map<string, ShopCategory>()
    categories.forEach((cat) => {
      searchKeyMap.set(cat.searchKey, cat)
    })
    return (searchKey: string) => searchKeyMap.get(searchKey)
  }, [categories])

  const getCategoryByComponentType = useMemo(() => {
    return (componentType: ComponentType, subtype?: OtherSubtype): ShopCategory | undefined => {
      if (componentType !== 'other') {
        return categories.find((cat) => cat.componentType === componentType)
      }

      if (!subtype) {
        return categories.find((cat) => cat.componentType === 'other')
      }

      for (const cat of categories) {
        if (cat.componentType !== 'other') continue
        if (!cat.subtypes) continue

        if (cat.subtypes.includes('*') || cat.subtypes.includes(subtype)) {
          return cat
        }
      }

      return categories.find((cat) => cat.componentType === 'other' && cat.subtypes?.includes('*'))
    }
  }, [categories])

  const getCategoryLabel = useMemo(() => {
    return (typeOrSearchKey: ComponentType | string, subtype?: OtherSubtype): string => {
      const bySearchKey = getCategoryBySearchKey(typeOrSearchKey)
      if (bySearchKey) return bySearchKey.name

      const byComponentType = getCategoryByComponentType(typeOrSearchKey as ComponentType, subtype)
      if (byComponentType) return byComponentType.name

      return (
        DEFAULT_SHOP_CATEGORIES.find((c) => c.componentType === typeOrSearchKey || c.searchKey === typeOrSearchKey)
          ?.name ?? ''
      )
    }
  }, [getCategoryBySearchKey, getCategoryByComponentType])

  const getSubtypesForCategory = useMemo(() => {
    return (category: ShopCategory): OtherSubtype[] => {
      if (category.componentType !== 'other') return []
      if (!category.subtypes) return []
      if (category.subtypes.includes('*')) return OTHER_SUBTYPES
      return category.subtypes.filter((s): s is OtherSubtype => s !== '*')
    }
  }, [])

  const isLoading = Boolean(isShopUpliftEnabled) && rawConfig === undefined

  const hasCustomConfig = useMemo(() => {
    if (!isShopUpliftEnabled) return false
    if (!rawConfig) return false
    if (!validateConfig(rawConfig)) return false
    const validCategories = rawConfig.categories.filter(validateCategory)
    return validCategories.length > 0
  }, [isShopUpliftEnabled, rawConfig])

  const isEmptyConfig = useMemo(() => {
    if (!isShopUpliftEnabled) return false
    if (rawConfig === undefined || rawConfig === null) return false
    return !hasCustomConfig
  }, [isShopUpliftEnabled, rawConfig, hasCustomConfig])

  return {
    config,
    categories,
    enabledCategories,
    isLoading,
    hasCustomConfig,
    isEmptyConfig,
    getCategoryBySearchKey,
    getCategoryByComponentType,
    getCategoryLabel,
    getSubtypesForCategory,
  }
}
